import * as React from 'react';
import { CMS_CACHE_TTL_HOURS, SectionKey } from './sections';
import { fetchVersions, fetchSection } from './client';
import { loadCache, saveCache, isStaleByTTL, clearCache } from './cache';

type State<T> = {
  data: T;
  version: number;
  loading: boolean;
  error?: string;
};

export function useCmsSection<T>(
  section: SectionKey,
  defaultData: T,
  apply: (defaults: T, patch: any) => T
) {
  const [state, setState] = React.useState<State<T>>({
    data: defaultData,
    version: 0,
    loading: true,
  });

  // Keep stable refs so refresh() doesn't change identity due to new objects/functions
  const defaultsRef = React.useRef(defaultData);
  const applyRef = React.useRef(apply);
  React.useEffect(() => { defaultsRef.current = defaultData; }, [defaultData]);
  React.useEffect(() => { applyRef.current = apply; }, [apply]);

  const refresh = React.useCallback(async (opts?: { force?: boolean }) => {
    try {
      setState(s => ({ ...s, loading: true, error: undefined }));

      const cached = loadCache<any>(section);
      const defaults = defaultsRef.current;
      const applyFn = applyRef.current;

      let baseData = defaults;
      let baseVersion = cached.version ?? 0;

      if (cached.data) {
        baseData = applyFn(defaults, cached.data);
      }

      const versions = await fetchVersions();
      const remoteVersion = (versions as any)[section] || 0;
      const ttlStale = isStaleByTTL(cached.fetchedAt, CMS_CACHE_TTL_HOURS);
      const outOfSync = remoteVersion !== (cached.version ?? 0);

      if (opts?.force || outOfSync || ttlStale || !cached.data) {
        const patch = await fetchSection<any>(section);
        const data = applyFn(defaults, patch);
        saveCache(section, remoteVersion, patch ?? null);
        setState({ data, version: remoteVersion, loading: false });
      } else {
        setState({ data: baseData, version: baseVersion, loading: false });
      }
    } catch (e: any) {
      setState(s => ({ ...s, loading: false, error: e?.message || 'Failed to load section' }));
    }
  }, [section]);

  // Only re-run when the section key changes
  React.useEffect(() => { refresh(); }, [refresh]);

  const clearAndRefresh = React.useCallback(async () => {
    clearCache(section);
    await refresh({ force: true });
  }, [section, refresh]);

  return { ...state, refresh, clearAndRefresh };
}
